/**
 * Aritmetica banilor. SINGURUL loc din aplicatie unde se rotunjeste o suma.
 *
 * De ce intregi si nu `number` zecimal: intreg scopul aplicatiei este
 * `Disponibil dupa plata = disponibil - de_plata - blocat`, comparat cu zero. Sursa Excel are
 * deja artefacte de tip 902644.1216 pentru ca `x 5.26` peste o suma EUR cu 2 zecimale da 4
 * zecimale. `SUM()` peste float in SQLite depinde de ordine, iar totalul zilei ajunge la CFO si
 * se re-exporta in Excel. Pragurile de comision (< 10.000 EUR, < 50.000 lei) sunt comparatii
 * exacte. D1/SQLite nu are DECIMAL.
 *
 * ATENTIE la overflow: cea mai mare suma din workbook e 32.704.124,77 lei => 3_270_412_477 bani.
 * Inmultit cu un curs e6 (5_260_000) da 1,7e16, PESTE Number.MAX_SAFE_INTEGER (9,007e15).
 * Deci orice inmultire suma x curs trece prin BigInt. Sumele in sine stau confortabil in
 * `number`, deci tipurile publice rn `number` — se serializeaza in JSON si D1 fara conversii.
 */

/** Suma in unitati minore (bani / eurocenti / filler). Intreg, semnat. */
export type Minor = number;
/** Curs de schimb scalat x 1e6. `5.2409` => 5_240_900. */
export type RateE6 = number;

export const RATE_SCALE = 1_000_000;
export const RATE_SCALE_BIG = 1_000_000n;

/** Politica de rotunjire, expusa ca dată ca sa poata fi inregistrata in `setting`. */
export const ROUNDING_POLICY = 'HALF_UP' as const;

export class MoneyError extends Error {
  override readonly name = 'MoneyError';
}

function assertSafeInteger(value: number, what: string): void {
  if (!Number.isFinite(value) || !Number.isInteger(value)) {
    throw new MoneyError(`${what} trebuie sa fie un intreg finit, primit ${value}`);
  }
  if (!Number.isSafeInteger(value)) {
    throw new MoneyError(`${what} depaseste intervalul sigur de intregi: ${value}`);
  }
}

/**
 * Impartire cu rotunjire half-up **departe de zero**, pe BigInt.
 * -2.5 => -3, 2.5 => 3. Aceeasi regula ca formatarea contabila romaneasca.
 */
export function divRoundHalfUp(numerator: bigint, denominator: bigint): bigint {
  if (denominator === 0n) throw new MoneyError('impartire la zero');
  const negative = numerator < 0n !== denominator < 0n;
  const n = numerator < 0n ? -numerator : numerator;
  const d = denominator < 0n ? -denominator : denominator;
  const quotient = n / d;
  const remainder = n % d;
  // remainder * 2 >= d  =>  fractia e >= 0.5  =>  se rotunjeste in sus
  const rounded = remainder * 2n >= d ? quotient + 1n : quotient;
  return negative ? -rounded : rounded;
}

function toSafeNumber(value: bigint, what: string): number {
  if (value > 9_007_199_254_740_991n || value < -9_007_199_254_740_991n) {
    throw new MoneyError(`${what} depaseste intervalul sigur de intregi: ${value}`);
  }
  return Number(value);
}

/**
 * Suma in valuta x curs => suma in lei (aceleasi unitati minore).
 * Reproduce exact `=D3*K$3` din foaia `Plati valutare`, cu rotunjire explicita.
 */
export function mulRate(amountMinor: Minor, rateE6: RateE6): Minor {
  assertSafeInteger(amountMinor, 'suma');
  assertSafeInteger(rateE6, 'cursul');
  if (rateE6 < 0) throw new MoneyError('cursul nu poate fi negativ');
  const product = BigInt(amountMinor) * BigInt(rateE6);
  return toSafeNumber(divRoundHalfUp(product, RATE_SCALE_BIG), 'suma convertita');
}

/** Suma in lei / curs => suma in valuta. Inversul lui `mulRate`. */
export function divRate(amountRonMinor: Minor, rateE6: RateE6): Minor {
  assertSafeInteger(amountRonMinor, 'suma');
  assertSafeInteger(rateE6, 'cursul');
  if (rateE6 <= 0) throw new MoneyError('cursul trebuie sa fie pozitiv pentru impartire');
  const numerator = BigInt(amountRonMinor) * RATE_SCALE_BIG;
  return toSafeNumber(divRoundHalfUp(numerator, BigInt(rateE6)), 'suma convertita');
}

/**
 * Converteste intre doua valute prin lei: `amount(from) -> amount(to)`.
 * Folosit pentru echivalentul EUR care decide pragul de comision de 10.000 EUR.
 * Se face intr-un singur pas (fara rotunjire intermediara in lei), ca sa nu piardem un ban
 * exact pe granita pragului.
 */
export function convert(amountMinor: Minor, fromRateE6: RateE6, toRateE6: RateE6): Minor {
  assertSafeInteger(amountMinor, 'suma');
  if (fromRateE6 < 0 || toRateE6 <= 0) {
    throw new MoneyError('cursuri invalide pentru conversie');
  }
  const numerator = BigInt(amountMinor) * BigInt(fromRateE6);
  return toSafeNumber(divRoundHalfUp(numerator, BigInt(toRateE6)), 'suma convertita');
}

/** Aplica un procent scalat x 1e6 (`0.15%` => 150_000). */
export function applyPercentE6(amountMinor: Minor, percentE6: number): Minor {
  assertSafeInteger(amountMinor, 'suma');
  assertSafeInteger(percentE6, 'procentul');
  const numerator = BigInt(amountMinor) * BigInt(percentE6);
  return toSafeNumber(divRoundHalfUp(numerator, RATE_SCALE_BIG * 100n), 'suma');
}

/** Suma peste o lista, cu verificare de overflow. Ordinea nu conteaza — sunt intregi. */
export function sumMinor(values: readonly (Minor | null | undefined)[]): Minor {
  let total = 0n;
  for (const v of values) {
    if (v === null || v === undefined) continue;
    assertSafeInteger(v, 'suma din lista');
    total += BigInt(v);
  }
  return toSafeNumber(total, 'totalul');
}

/** `max(a - b, 0)` — regula "nu se plateste marfa nevanduta" (sold - stoc). */
export function clampAtZero(value: Minor): Minor {
  return value > 0 ? value : 0;
}

// ---------------------------------------------------------------------------
// Conversii intre unitati minore si zecimale
// ---------------------------------------------------------------------------

/** Cate unitati minore are o unitate majora. RON/EUR/USD/GBP = 100, HUF = 100 (filler). */
export const DEFAULT_MINOR_SCALE = 100;

/**
 * Converteste un float (asa cum l-a produs Excel) in unitati minore.
 * Aici se pierd zecimalele 3 si 4 ale valorilor Excel de tip 902644.1216 — de aceea fiecare
 * rand importat pastreaza si floatul original in `legacy_*_real`, iar raportul de import
 * verifica |Σlegacy - Σminor/100| <= 0,005 x n per zi.
 */
export function fromFloat(value: number, minorScale = DEFAULT_MINOR_SCALE): Minor {
  if (!Number.isFinite(value)) throw new MoneyError(`valoare non-finita: ${value}`);
  // Trecem prin reprezentarea zecimala completa, nu prin `value * minorScale`:
  // inmultirea ar depasi precizia float exact la sumele mari, iar 10 zecimale sunt suficiente
  // pentru orice valoare produsa de Excel, fara dubla rotunjire.
  const minor = parseRoDecimal(value.toFixed(10), minorScale);
  if (minor === null) throw new MoneyError(`nu pot converti valoarea ${value}`);
  return minor;
}

/** Unitati minore -> float, pentru export si comparatii cu valorile legacy. */
export function toFloat(amountMinor: Minor, minorScale = DEFAULT_MINOR_SCALE): number {
  assertSafeInteger(amountMinor, 'suma');
  return amountMinor / minorScale;
}

/** Curs ca float (5.2409) -> e6. */
export function rateFromFloat(value: number): RateE6 {
  if (!Number.isFinite(value) || value < 0) {
    throw new MoneyError(`curs invalid: ${value}`);
  }
  return Math.round(value * RATE_SCALE);
}

/** Curs e6 -> float. */
export function rateToFloat(rateE6: RateE6): number {
  return rateE6 / RATE_SCALE;
}

/**
 * BNR publica unele cursuri la 100 de unitati (HUF, JPY, KRW). Normalizam la 1 unitate,
 * pastrand separat `multiplier` si valoarea bruta, ca cifra publicata sa rmn verificabila.
 */
export function normalizeBnrRate(rawValue: string, multiplier: number): RateE6 {
  const parsed = Number.parseFloat(rawValue);
  if (!Number.isFinite(parsed)) {
    throw new MoneyError(`curs BNR invalid: ${rawValue}`);
  }
  if (!Number.isInteger(multiplier) || multiplier < 1) {
    throw new MoneyError(`multiplicator BNR invalid: ${multiplier}`);
  }
  // Scalam la e6 inainte de a imparti, ca sa nu pierdem precizie pe HUF (0,013).
  const scaled = BigInt(Math.round(parsed * RATE_SCALE));
  return toSafeNumber(divRoundHalfUp(scaled, BigInt(multiplier)), 'curs BNR');
}

// ---------------------------------------------------------------------------
// Parsare de intrare — tolerant la felul in care tasteaza un contabil roman
// ---------------------------------------------------------------------------

export interface ParsedAmount {
  minor: Minor;
  /** Expresia originala, daca intrarea a inceput cu `=`. Se salveaza in `amount_expression`. */
  expression: string | null;
  /** Termenii unei expresii, in unitati minore, semnati. */
  terms: Minor[];
}

const THOUSANDS_GROUP = /^\d{1,3}(\.\d{3})+$/;

/**
 * Parseaza un numar zecimal scris in oricare din formele pe care le folosesc:
 * `1.234,56` (ro), `1234.56` (tastatura numerica), `1234,56`, `1 234,56`, `250000`.
 *
 * Ambiguitatea reala e `1.234`: in ro poate fi "o mie doua sute treizeci si patru" sau
 * "1,234". Regula aplicata: un singur punct cu exact 3 cifre dupa el, si fara virgula in
 * text, se citeste ca separator de mii doar daca partea intreaga are 1-3 cifre si textul
 * arata ca un grup complet de mii (`1.234`, `12.345`, `1.234.567`). Altfel punctul e zecimal.
 */
export function parseRoDecimal(input: string, minorScale = DEFAULT_MINOR_SCALE): Minor | null {
  let text = input.trim();
  if (text === '') return null;

  // Scoatem spatiile si simbolurile de moneda. JS include deja NBSP si toata categoria
  // Unicode Zs in clasa de spatiu, deci acopera si separatorii veniti prin copy-paste.
  text = text.replace(/\s/g, '').replace(/(lei|ron|eur|usd|huf|gbp|€|\$|£)/gi, '');
  if (text === '') return null;

  let negative = false;
  if (text.startsWith('(') && text.endsWith(')')) {
    negative = true;
    text = text.slice(1, -1);
  }
  if (text.startsWith('+')) text = text.slice(1);
  if (text.startsWith('-')) {
    negative = !negative;
    text = text.slice(1);
  }
  if (text === '') return null;

  const hasComma = text.includes(',');
  const dotCount = (text.match(/\./g) ?? []).length;

  let normalized: string;
  if (hasComma) {
    // virgula e mereu separatorul zecimal; punctele sunt separatori de mii
    const lastComma = text.lastIndexOf(',');
    const intPart = text.slice(0, lastComma).replace(/\./g, '');
    const fracPart = text.slice(lastComma + 1);
    if (fracPart.includes(',')) return null;
    normalized = `${intPart === '' ? '0' : intPart}.${fracPart === '' ? '0' : fracPart}`;
  } else if (dotCount > 1 || (dotCount === 1 && THOUSANDS_GROUP.test(text))) {
    normalized = text.replace(/\./g, '');
  } else {
    normalized = text;
  }

  if (!/^\d*(\.\d*)?$/.test(normalized) || normalized === '' || normalized === '.') return null;

  const [intRaw = '0', fracRaw = ''] = normalized.split('.');
  const scaleDigits = String(minorScale).length - 1;
  const frac = fracRaw.padEnd(scaleDigits + 1, '0');
  const kept = frac.slice(0, scaleDigits);
  const nextDigit = frac.charCodeAt(scaleDigits) - 48;

  let minor = BigInt(intRaw === '' ? '0' : intRaw) * BigInt(minorScale) + BigInt(kept || '0');
  if (nextDigit >= 5) minor += 1n; // half-up pe zecimala urmatoare
  const value = toSafeNumber(minor, 'suma introdusa');
  return negative ? -value : value;
}

/** Parseaza un curs scris manual (4 zecimale in practica). */
export function parseRoRate(input: string): RateE6 | null {
  const minor = parseRoDecimal(input, RATE_SCALE);
  if (minor === null || minor < 0) return null;
  return minor;
}

/**
 * Parseaza o intrare care poate fi o expresie in stil Excel: `=2515.67+2501.07`,
 * `=56274-28000`, `=34824.86+15`. Chiar asa tasteaza azi — 55 de celule din foaia valutara si
 * 69 din foaia principala sunt expresii. Salvam si expresia si termenii, ca sa se poata afisa
 * "derivat din" si ca exportul sa regenereze formula.
 *
 * Nu accepta referinte de celula: la runtime nu exista celule. Referintele din workbook
 * (`=812318.67-500000-AQ17-AM17`) se rezolva o singura data, la import.
 */
export function parseAmountInput(
  input: string,
  minorScale = DEFAULT_MINOR_SCALE,
): ParsedAmount | null {
  const raw = input.trim();
  if (raw === '') return null;

  if (!raw.startsWith('=')) {
    const minor = parseRoDecimal(raw, minorScale);
    return minor === null ? null : { minor, expression: null, terms: [minor] };
  }

  const body = raw.slice(1).replace(/\s/g, '');
  if (body === '') return null;
  // spargem pe + / - pastrand semnul, fara a rupe un numar
  const tokens = body.match(/[+-]?[^+-]+/g);
  if (!tokens || tokens.length === 0) return null;

  const terms: Minor[] = [];
  for (const token of tokens) {
    let sign = 1;
    let rest = token;
    while (rest.startsWith('+') || rest.startsWith('-')) {
      if (rest.startsWith('-')) sign = -sign;
      rest = rest.slice(1);
    }
    if (rest === '') return null;
    const parsed = parseRoDecimal(rest, minorScale);
    if (parsed === null) return null;
    terms.push(sign * parsed);
  }

  return { minor: sumMinor(terms), expression: raw, terms };
}

/** Reconstruieste o expresie Excel din termeni, pentru export. */
export function formatExpression(terms: readonly Minor[], minorScale = DEFAULT_MINOR_SCALE): string {
  if (terms.length === 0) return '';
  const parts = terms.map((t, i) => {
    const abs = Math.abs(t);
    const text = (abs / minorScale).toString();
    if (i === 0) return t < 0 ? `-${text}` : text;
    return t < 0 ? `-${text}` : `+${text}`;
  });
  return `=${parts.join('')}`;
}
